// The three sub-branches of the Internal Review (SEP-293 section 7).
//
// Stage 4 of the onboarding pipeline is the Internal Review. It holds three
// ordered sub-branches: 1. the Fact Find, 2. the Vulnerability assessment and
// 3. the Suitability assessment. This module derives the state of each branch
// from the records that the server reads. It is pure, so the case panel, the
// page header of each branch and the gates of the server read one answer.
//
// The order is a gate at each completion point, and not a lock on each page.
// The IM can open each page at any time and read it.

import { z } from 'zod';

import {
    suitabilityLifecycleSchema,
    type SuitabilityLifecycle,
} from './suitability-assessment-lifecycle';

export const INTERNAL_REVIEW_BRANCH_STATES = [
    'not_started',
    'in_progress',
    'complete',
    'needs_review',
] as const;
export const internalReviewBranchStateSchema = z.enum(INTERNAL_REVIEW_BRANCH_STATES);
export type InternalReviewBranchState = z.infer<typeof internalReviewBranchStateSchema>;

/** The explicit outcome of a vulnerability assessment (SEP-293 section 7). */
export const VULNERABILITY_OUTCOMES = [
    'not_vulnerable',
    'potentially_vulnerable',
    'vulnerable',
] as const;
export const vulnerabilityOutcomeSchema = z.enum(VULNERABILITY_OUTCOMES);
export type VulnerabilityOutcome = z.infer<typeof vulnerabilityOutcomeSchema>;

/** The words of each outcome. UI copy, for the approval of the user. */
export const VULNERABILITY_OUTCOME_LABELS = Object.freeze(
    Object.fromEntries([
        ['not_vulnerable', 'Not vulnerable'],
        ['potentially_vulnerable', 'Potentially vulnerable'],
        ['vulnerable', 'Vulnerable'],
    ]),
) as Readonly<Record<VulnerabilityOutcome, string>>;

/** One reason that holds the suitability branch, the sign-off or the move to Signing. */
export const internalReviewBlockerSchema = z.object({
    code: z.string(),
    message: z.string(),
    flagId: z.string().optional(),
});
export type InternalReviewBlocker = z.infer<typeof internalReviewBlockerSchema>;

export const internalReviewBranchesSchema = z.object({
    factFind: z.object({
        state: internalReviewBranchStateSchema,
        /** The labels of the blank mandatory fields. */
        gaps: z.array(z.string()),
    }),
    vulnerability: z.object({
        state: internalReviewBranchStateSchema,
        reference: z.string().nullable(),
        outcome: vulnerabilityOutcomeSchema.nullable(),
    }),
    suitability: z.object({
        state: internalReviewBranchStateSchema,
        lifecycle: suitabilityLifecycleSchema.nullable(),
        flagCounts: z.object({ red: z.number(), amber: z.number(), info: z.number() }),
        blockers: z.array(internalReviewBlockerSchema),
    }),
});
export type InternalReviewBranches = z.infer<typeof internalReviewBranchesSchema>;

/** What the derivation reads about the fact find of the client. */
export interface FactFindBranchInput {
    /** The approved version, when one exists. */
    readonly approvedVersionId: string | null;
    readonly approvedAt: string | null;
    /** A working version that is newer than the approval. */
    readonly currentVersionId: string | null;
    /** The newest amendment of the approved version that came after the approval. */
    readonly amendedAfterApprovalAt: string | null;
    /** The labels of the blank mandatory fields of the newest version. */
    readonly mandatoryGaps: readonly string[];
    /** The case holds the paper override ("Mark fact find complete"). */
    readonly overrideComplete: boolean;
}

/** What the derivation reads about the newest vulnerability assessment. */
export interface VulnerabilityBranchInput {
    readonly reference: string | null;
    readonly status: 'draft' | 'completed' | 'superseded';
    readonly outcome: VulnerabilityOutcome | null;
    readonly completedAt: string | null;
}

/** What the derivation reads about the newest suitability assessment. */
export interface SuitabilityBranchInput {
    readonly lifecycle: SuitabilityLifecycle;
    /** A change of a source reopened the signed-off review (SEP-293 section 7). */
    readonly reopenedBySourceChange: boolean;
}

export interface InternalReviewBranchInput {
    readonly factFind: FactFindBranchInput | null;
    readonly vulnerability: VulnerabilityBranchInput | null;
    readonly suitability: SuitabilityBranchInput | null;
    readonly flagCounts?: { readonly red: number; readonly amber: number; readonly info: number };
    readonly blockers?: readonly InternalReviewBlocker[];
}

function factFindState(ff: FactFindBranchInput | null): InternalReviewBranchState {
    if (!ff) return 'not_started';
    if (ff.approvedVersionId) {
        if (ff.currentVersionId || ff.amendedAfterApprovalAt) return 'needs_review';
        return ff.mandatoryGaps.length === 0 ? 'complete' : 'in_progress';
    }
    if (ff.overrideComplete) return 'complete';
    return ff.currentVersionId ? 'in_progress' : 'not_started';
}

function vulnerabilityState(
    va: VulnerabilityBranchInput | null,
    ff: FactFindBranchInput | null,
): InternalReviewBranchState {
    if (!va || va.status === 'superseded') return 'not_started';
    if (va.status === 'draft') return 'in_progress';
    // A completed assessment that read an older fact find needs a second look.
    const changedAt = ff?.amendedAfterApprovalAt ?? ff?.approvedAt ?? null;
    if (changedAt && va.completedAt && changedAt > va.completedAt) return 'needs_review';
    return va.outcome ? 'complete' : 'in_progress';
}

function suitabilityState(sa: SuitabilityBranchInput | null): InternalReviewBranchState {
    if (!sa) return 'not_started';
    if (sa.reopenedBySourceChange && sa.lifecycle === 'draft') return 'needs_review';
    if (sa.lifecycle === 'approved') return 'complete';
    // A signed-off review is not complete while it waits for a peer reviewer or
    // for Compliance. Only the Compliance approval completes the branch (SEP-344).
    return 'in_progress';
}

/** The state of each sub-branch of the Internal Review. */
export function deriveInternalReviewBranches(
    input: InternalReviewBranchInput,
): InternalReviewBranches {
    const blockers = [...(input.blockers ?? [])];
    return {
        factFind: {
            state: factFindState(input.factFind),
            gaps: [...(input.factFind?.mandatoryGaps ?? [])],
        },
        vulnerability: {
            state: vulnerabilityState(input.vulnerability, input.factFind),
            reference: input.vulnerability?.reference ?? null,
            outcome: input.vulnerability?.outcome ?? null,
        },
        suitability: {
            state: suitabilityState(input.suitability),
            lifecycle: input.suitability?.lifecycle ?? null,
            flagCounts: { red: 0, amber: 0, info: 0, ...input.flagCounts },
            blockers,
        },
    };
}

/**
 * The hint of a branch whose earlier branch is not complete, or null. The
 * button of the branch stays active, so the IM can read the page.
 */
export function internalReviewOrderHint(
    branches: InternalReviewBranches,
    branch: 'vulnerability' | 'suitability',
): string | null {
    if (branch === 'vulnerability') {
        return branches.factFind.state === 'complete' ? null : 'Complete the Fact Find first';
    }
    if (branches.factFind.state !== 'complete') return 'Complete the Fact Find first';
    return branches.vulnerability.state === 'complete'
        ? null
        : 'Complete the Vulnerability assessment first';
}
